import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
	inject,
	DestroyRef,
	signal,
	computed,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationsService } from '@services/notifications';
import { AuthService } from '@services/auth';
import { SocketService } from '@services/socket.service';
import { InvestingManagerService } from '@services/data-managers/investing-manager.service';
import { InvestingAlertPayload } from '@models/investing';
import { AlertNotification } from '@models/alert';
import { ThemeService } from '@services/theme.service';
import { TranslateModule } from '@ngx-translate/core';
import { CMS_NAV_ITEMS, NavItem, navItemsForRole } from '../nav.config';
import { AlertManagerService } from '@services/data-managers/alert-manager.service';
import { canAccessModule } from '../module-access.config';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-cms-sidenav',
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.less'],
	standalone: true,
	imports: [RouterLink, RouterLinkActive, MatIcon, MatBadgeModule, TranslateModule],
})
export class SidenavComponent {
	@Output() closeMenuEmitter: EventEmitter<void> = new EventEmitter();
	@Output() collapseToggle: EventEmitter<void> = new EventEmitter();
	@Input() collapsed = signal(false);

	private readonly _ns = inject(NotificationsService);
	private readonly _as = inject(AuthService);
	private readonly _r = inject(Router);
	private readonly _socket = inject(SocketService);
	private readonly _im = inject(InvestingManagerService);
	private readonly _snackBar = inject(MatSnackBar);
	private readonly destroyRef = inject(DestroyRef);
	readonly themeService = inject(ThemeService);

	readonly unreadAlerts = this._im.unreadInvestingAlerts.asReadonly();
	private readonly _alerts = inject(AlertManagerService);
	/** Unread notification count (alert_history) for the Powiadomienia nav badge. */
	readonly unreadNotifications = this._alerts.unreadCount.asReadonly();

	/** Main nav tabs (shared config) — everything except admin-gated entries. */
	readonly navItems = computed(() => navItemsForRole(this._as.user()?.role));
	/** Admin-gated tabs, rendered under a divider only for admins. */
	readonly adminItems: NavItem[] = CMS_NAV_ITEMS.filter((i) => i.adminOnly);

	constructor() {
		// Seed the Powiadomienia badge from the DB and keep it live over WebSocket.
		this._socket.subscribeToAlerts();
		this._alerts.getHistory().subscribe();
		toObservable(this._socket.latestAlert)
			.pipe(
				filter((a): a is AlertNotification => a !== null),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((alert) => {
				this._alerts.unreadCount.update((n) => n + 1);
				this._snackBar.open(alert.title, 'OK', {
					duration: 6000,
					panelClass: ['snack-alert'],
				});
				this._browserNotify(alert.title, alert.message);
			});

		// Show snackbar and increment badge whenever a new investing alert arrives
		toObservable(this._socket.latestInvestingAlert)
			.pipe(
				filter((a): a is InvestingAlertPayload => a !== null),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((alert) => {
				this._im.unreadInvestingAlerts.update((n) => n + 1);
				const dir = alert.direction === 'long' ? '▲ Long' : '▼ Short';
				this._snackBar.open(
					`📊 ${alert.symbol}: ${dir} (score ${alert.score.toFixed(0)})`,
					'OK',
					{ duration: 6000, panelClass: ['snack-investing-alert'] },
				);
			});

		// Clear badge when user navigates to /investing
		this._r.events
			.pipe(
				filter((e): e is NavigationEnd => e instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((e) => {
				if (e.urlAfterRedirects.startsWith('/investing')) {
					this._im.unreadInvestingAlerts.set(0);
				}
			});
	}

	get isAdmin(): boolean {
		return this._as.user()?.role === 'admin';
	}

	/** Fire an OS/browser notification for an alert (foreground; needs permission). */
	private _browserNotify(title: string, body: string): void {
		if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
			return;
		}
		try {
			// eslint-disable-next-line no-new
			new Notification(title, { body, icon: 'favicon.ico', tag: 'cryptionis-alert' });
		} catch {
			/* Notification constructor can throw on some platforms — ignore. */
		}
	}

	/**
	 * A gated tab the user's tier can't access (admins are never locked).
	 * Locked tabs stay visible but dimmed with a lock icon; clicking them is
	 * intercepted by `moduleGuard` → the upsell page.
	 */
	isLocked(item: NavItem): boolean {
		if (this.isAdmin) {
			return false;
		}

		return !canAccessModule(this._as.user(), item.module);
	}

	get isCollapsed(): boolean {
		return this.collapsed();
	}

	onMenuItemClick(): void {
		this.closeMenuEmitter.emit();
	}

	onNotificationsClick(): void {
		this._ns.toggleNotificationsSidebarStatus();
	}

	onCollapseClick(): void {
		this.collapseToggle.emit();
	}

	onThemeToggle(): void {
		this.themeService.toggle();
	}

	onLogoutClick(): void {
		this._as
			.logout()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this._r.navigate(['login']);
			});
	}
}
